import { useMemo, useState } from 'react'
import { Plus, Trash2, Settings2, ShieldAlert } from 'lucide-react'
import { adminMutate, Select } from './primitives.js'
import { Field, NumberInput, SwitchRow, TextInput } from './form.js'
import { useSaveBar } from '../shell/editing.js'
import { baissesDeProtection } from './backup-lisible.js'
import { PcBureauCard, type EtatCopiePc } from './PcBureauCard.js'

/**
 * RÉGLAGES DE LA SAUVEGARDE AUTOMATIQUE — une seule Save Bar pour tout.
 *
 * Règle du propriétaire : chaque heure, chaque durée de conservation, chaque
 * seuil d'alerte se règle ici ; le code ne garde que des défauts de secours.
 *
 * Baisser la protection (éteindre, retirer ou déplacer une heure ou le
 * test, garder moins longtemps, faire attendre l'alarme) demande au serveur
 * une ré-authentification forte ; `adminMutate` la propose d'elle-même et
 * reprend l'enregistrement ensuite — rien de la saisie n'est perdu. Ce qui
 * baisse est écrit AVANT, sous les réglages : le dialogue de confirmation,
 * lui, ne sait pas dire pourquoi il s'ouvre.
 *
 * Lot C : le panneau rend AUSSI la carte « PC du bureau », juste après les
 * réglages. Même réglage `backup`, même PUT, même Save Bar : une seconde
 * barre écrirait la même ligne et écraserait la saisie de la première.
 */

export interface ReglagesSauvegarde {
  enabled: boolean
  slots: { time: string; files: boolean }[]
  retention: { daily_days: number; monthly_months: number }
  restore_test: { enabled: boolean; weekday: number; time: string }
  alert_after_hours: number
  restore_test_alert_days: number
  key_escrowed_on: string | null
  /** `enabled_since` est posé par le serveur ; il n'est jamais renvoyé. */
  pc: { enabled: boolean; keep: number; alert_after_days: number; enabled_since?: string | null }
}

const HEURE_RX = /^([01]\d|2[0-3]):([0-5]\d)$/
const JOUR_RX = /^\d{4}-\d{2}-\d{2}$/

const JOURS = [
  { value: '1', label: 'Lundi' }, { value: '2', label: 'Mardi' }, { value: '3', label: 'Mercredi' },
  { value: '4', label: 'Jeudi' }, { value: '5', label: 'Vendredi' }, { value: '6', label: 'Samedi' },
  { value: '7', label: 'Dimanche' },
]

const CONTENU = [
  { value: 'files', label: 'Base + fichiers' },
  { value: 'db', label: 'Base seule' },
]

/** Saisie : tout en texte, pour qu'une case vidée reste vide le temps de la retaper. */
interface Saisie {
  enabled: boolean
  slots: { time: string; files: boolean }[]
  daily: string
  monthly: string
  testEnabled: boolean
  testDay: string
  testTime: string
  alertH: string
  alertD: string
  key: string
  pcEnabled: boolean
  pcKeep: string
  pcAlert: string
}

function versSaisie(r: ReglagesSauvegarde): Saisie {
  return {
    enabled: r.enabled,
    slots: r.slots.map((s) => ({ ...s })),
    daily: String(r.retention.daily_days),
    monthly: String(r.retention.monthly_months),
    testEnabled: r.restore_test.enabled,
    testDay: String(r.restore_test.weekday),
    testTime: r.restore_test.time,
    alertH: String(r.alert_after_hours),
    alertD: String(r.restore_test_alert_days),
    key: r.key_escrowed_on ?? '',
    pcEnabled: r.pc.enabled,
    pcKeep: String(r.pc.keep),
    pcAlert: String(r.pc.alert_after_days),
  }
}

function entier(t: string): number | null {
  return /^\d{1,4}$/.test(t.trim()) ? Number(t.trim()) : null
}

function depuisSaisie(s: Saisie): ReglagesSauvegarde {
  return {
    enabled: s.enabled,
    slots: [...s.slots].map((x) => ({ time: x.time.trim(), files: x.files })).sort((a, b) => a.time.localeCompare(b.time)),
    retention: { daily_days: entier(s.daily) ?? -1, monthly_months: entier(s.monthly) ?? -1 },
    restore_test: { enabled: s.testEnabled, weekday: Number(s.testDay), time: s.testTime.trim() },
    alert_after_hours: entier(s.alertH) ?? -1,
    restore_test_alert_days: entier(s.alertD) ?? -1,
    key_escrowed_on: s.key.trim() === '' ? null : s.key.trim(),
    pc: { enabled: s.pcEnabled, keep: entier(s.pcKeep) ?? -1, alert_after_days: entier(s.pcAlert) ?? -1 },
  }
}

function invalide(s: Saisie, b: Bornes): string | null {
  if (s.slots.length < 1) return 'Gardez au moins une heure de sauvegarde par jour.'
  if (s.slots.some((x) => !HEURE_RX.test(x.time.trim()))) return 'Chaque heure de sauvegarde s’écrit HH:MM, par exemple 07:30.'
  if (new Set(s.slots.map((x) => x.time.trim())).size !== s.slots.length) {
    return 'Deux sauvegardes sont prévues à la même heure : gardez-en une seule.'
  }
  const d = entier(s.daily)
  if (d === null || d < b.daily_days.min || d > b.daily_days.max) {
    return `Les sauvegardes quotidiennes se gardent de ${b.daily_days.min} à ${b.daily_days.max} jours.`
  }
  const m = entier(s.monthly)
  if (m === null || m < b.monthly_months.min || m > b.monthly_months.max) {
    return `Les sauvegardes mensuelles se gardent de ${b.monthly_months.min} à ${b.monthly_months.max} mois.`
  }
  if (!HEURE_RX.test(s.testTime.trim())) return 'L’heure du test de restauration s’écrit HH:MM, par exemple 08:00.'
  const h = entier(s.alertH)
  if (h === null || h < b.alert_after_hours.min || h > b.alert_after_hours.max) {
    return `L’alerte « aucune sauvegarde » se règle de ${b.alert_after_hours.min} à ${b.alert_after_hours.max} heures.`
  }
  const j = entier(s.alertD)
  if (j === null || j < b.restore_test_alert_days.min || j > b.restore_test_alert_days.max) {
    return `L’alerte « test trop ancien » se règle de ${b.restore_test_alert_days.min} à ${b.restore_test_alert_days.max} jours.`
  }
  const k = entier(s.pcKeep)
  if (k === null || k < b.pc_keep.min || k > b.pc_keep.max) {
    return `Le PC du bureau garde de ${b.pc_keep.min} à ${b.pc_keep.max} copies.`
  }
  const a = entier(s.pcAlert)
  if (a === null || a < b.pc_alert_after_days.min || a > b.pc_alert_after_days.max) {
    return `L’alerte « PC du bureau » se règle de ${b.pc_alert_after_days.min} à ${b.pc_alert_after_days.max} jours.`
  }
  if (s.key.trim() !== '' && !JOUR_RX.test(s.key.trim())) {
    return 'La date de mise à l’abri de la clé maître n’est pas valide. Choisissez-la dans le calendrier, ou laissez vide si elle ne l’a pas été.'
  }
  return null
}

interface Borne { min: number; max: number }
export interface Bornes {
  slots: Borne; daily_days: Borne; monthly_months: Borne; alert_after_hours: Borne; restore_test_alert_days: Borne
  pc_keep: Borne; pc_alert_after_days: Borne
}

export function BackupSettingsPanel(props: {
  initial: ReglagesSauvegarde
  bornes: Bornes
  /** État de la copie PC (lot C), pour la carte « PC du bureau ». */
  pc: EtatCopiePc
  onEnregistre: () => void
}) {
  const [s, setS] = useState<Saisie>(() => versSaisie(props.initial))
  const maj = (p: Partial<Saisie>) => setS((x) => ({ ...x, ...p }))

  const serveur = useMemo(() => JSON.stringify(depuisSaisie(versSaisie(props.initial))), [props.initial])
  const courant = JSON.stringify(depuisSaisie(s))
  const dirty = courant !== serveur
  const raison = invalide(s, props.bornes)
  // Chaque carte annonce SES baisses : celles de la copie PC sous la carte
  // « PC du bureau », les autres sous « Réglages ».
  const saisi = depuisSaisie(s)
  const baisses = dirty && raison === null ? baissesDeProtection(props.initial, { ...saisi, pc: props.initial.pc }) : []
  const baissesPc = dirty && raison === null ? baissesDeProtection(props.initial, { ...props.initial, pc: saisi.pc }) : []

  useSaveBar({
    dirty,
    invalid: raison,
    successMessage: 'Réglages de sauvegarde enregistrés.',
    onSave: async () => {
      const r = await adminMutate<{ error?: string; message?: string; lowered?: string[] }>(
        'PUT', '/api/v1/system/backups/settings', depuisSaisie(s))
      if (r.status >= 200 && r.status < 300) { props.onEnregistre(); return true }
      if (r.data.error === 'STEPUP_REQUIRED') {
        return 'Confirmation d’identité annulée : rien n’a été enregistré.'
          + (r.data.lowered && r.data.lowered.length > 0 ? ` (Ce changement réduit la protection : ${r.data.lowered.join(' ; ')}.)` : '')
      }
      return r.data.message ?? 'Le serveur a refusé ces réglages.'
    },
    onCancel: () => setS(versSaisie(props.initial)),
  })

  const majCreneau = (i: number, p: Partial<{ time: string; files: boolean }>) =>
    setS((x) => ({ ...x, slots: x.slots.map((c, k) => k === i ? { ...c, ...p } : c) }))

  const ajouter = () => {
    // Une heure libre plausible : midi, sinon la première demi-heure libre.
    const prises = new Set(s.slots.map((c) => c.time))
    const libre = ['12:00', '13:00', '15:00', '23:00'].find((h) => !prises.has(h)) ?? '00:30'
    setS((x) => ({ ...x, slots: [...x.slots, { time: libre, files: false }] }))
  }

  return (
    <>
    <section className="lc-panel">
      <header className="lc-panel__head">
        <Settings2 size={18} aria-hidden="true" />
        <h2 className="lc-panel__title">Réglages</h2>
      </header>
      <div className="lc-panel__body bk-settings">
        <SwitchRow
          checked={s.enabled}
          onChange={(v) => maj({ enabled: v })}
          title="Sauvegarde automatique"
          desc={s.enabled
            ? 'Gestion se sauvegarde de lui-même aux heures ci-dessous, sur le second disque du serveur.'
            : 'Éteinte : plus aucune copie automatique, et l’alarme des sauvegardes le signalera.'}
        />

        <div className="bk-group">
          <h3 className="bk-group__title">Heures de sauvegarde</h3>
          <p className="bk-group__help">
            « Base + fichiers » emporte aussi les pièces comptables, les photos et les captures ;
            « base seule » est plus rapide. La clé maître est copiée à chaque fois.
          </p>
          <div className="bk-slots">
            {s.slots.map((c, i) => (
              <div className="bk-slot" key={i}>
                <div>
                  <TextInput value={c.time} maxLength={5} ariaLabel={`Heure de la sauvegarde ${i + 1}`}
                    placeholder="07:30" onChange={(v) => majCreneau(i, { time: v })} />
                </div>
                <div>
                  <Select value={c.files ? 'files' : 'db'} options={CONTENU}
                    ariaLabel={`Contenu de la sauvegarde ${i + 1}`}
                    onChange={(v) => majCreneau(i, { files: v === 'files' })} />
                </div>
                <button type="button" className="lc-btn lc-btn--ghost bk-slot__remove"
                  disabled={s.slots.length <= 1}
                  aria-label={`Retirer la sauvegarde de ${c.time}`}
                  onClick={() => setS((x) => ({ ...x, slots: x.slots.filter((_, k) => k !== i) }))}>
                  <Trash2 size={14} aria-hidden="true" /> Retirer
                </button>
              </div>
            ))}
          </div>
          {s.slots.length < props.bornes.slots.max ? (
            <button type="button" className="lc-btn lc-btn--ghost bk-add" onClick={ajouter}>
              <Plus size={14} aria-hidden="true" /> Ajouter une heure
            </button>
          ) : null}
        </div>

        <div className="bk-group">
          <h3 className="bk-group__title">Conservation</h3>
          <div className="bk-fields">
            <Field label="Sauvegardes de chaque jour gardées" helper={`De ${props.bornes.daily_days.min} à ${props.bornes.daily_days.max} jours.`}>
              <NumberInput value={s.daily} onChange={(v) => maj({ daily: v })} unit="jours"
                min={props.bornes.daily_days.min} max={props.bornes.daily_days.max} ariaLabel="Jours de sauvegardes quotidiennes gardés" />
            </Field>
            <Field label="Une sauvegarde par mois gardée pendant" helper="0 = aucune sauvegarde mensuelle.">
              <NumberInput value={s.monthly} onChange={(v) => maj({ monthly: v })} unit="mois"
                min={props.bornes.monthly_months.min} max={props.bornes.monthly_months.max} ariaLabel="Mois de sauvegardes mensuelles gardés" />
            </Field>
          </div>
        </div>

        <div className="bk-group">
          <h3 className="bk-group__title">Test de restauration</h3>
          <SwitchRow
            checked={s.testEnabled}
            onChange={(v) => maj({ testEnabled: v })}
            title="Prouver chaque semaine qu’une copie se restaure"
            desc="La dernière sauvegarde est restaurée dans une base jetable, puis contrôlée. Gestion n’est pas touché."
          />
          <div className="bk-fields">
            <Field label="Jour">
              <Select value={s.testDay} options={JOURS} ariaLabel="Jour du test de restauration"
                onChange={(v) => maj({ testDay: v })} />
            </Field>
            <Field label="Heure">
              <div className="bk-time">
                <TextInput value={s.testTime} maxLength={5} placeholder="08:00" ariaLabel="Heure du test de restauration"
                  onChange={(v) => maj({ testTime: v })} />
              </div>
            </Field>
          </div>
        </div>

        <div className="bk-group">
          <h3 className="bk-group__title">Quand prévenir</h3>
          <div className="bk-fields">
            <Field label="Aucune sauvegarde réussie depuis" helper={`Au-delà de ${s.alertH} h sans sauvegarde automatique réussie, l’alarme des sauvegardes prévient.`}>
              <NumberInput value={s.alertH} onChange={(v) => maj({ alertH: v })} unit="h"
                min={props.bornes.alert_after_hours.min} max={props.bornes.alert_after_hours.max} ariaLabel="Heures sans sauvegarde avant alerte" />
            </Field>
            <Field label="Dernier test réussi plus ancien que" helper="Au-delà, la preuve qu’une copie se restaure est trop vieille.">
              <NumberInput value={s.alertD} onChange={(v) => maj({ alertD: v })} unit="jours"
                min={props.bornes.restore_test_alert_days.min} max={props.bornes.restore_test_alert_days.max} ariaLabel="Jours avant alerte sur le test" />
            </Field>
          </div>
        </div>

        <div className="bk-group">
          <h3 className="bk-group__title">Clé maître</h3>
          <div className="bk-fields">
            <Field label="Copiée hors du serveur le"
              helper="Sans cette clé, une sauvegarde restaurée ne peut plus lire les mots de passe des services. Laissez vide si elle n’a pas été mise à l’abri.">
              <div className="bk-date">
                <TextInput type="date" value={s.key} max={new Date().toLocaleDateString('sv-SE')}
                  ariaLabel="Date de mise à l’abri de la clé maître" onChange={(v) => maj({ key: v })} />
              </div>
            </Field>
          </div>
        </div>

        {baisses.length > 0 ? (
          <div className="bk-callout bk-callout--warn" role="status">
            <ShieldAlert size={16} aria-hidden="true" />
            <div>
              <strong>Ces changements réduisent la protection</strong> — votre identité vous sera
              redemandée à l’enregistrement :
              <ul className="bk-callout__list">
                {baisses.map((b) => <li key={b}>{b}</li>)}
              </ul>
            </div>
          </div>
        ) : null}
      </div>
    </section>

    <PcBureauCard
      saisie={{ enabled: s.pcEnabled, keep: s.pcKeep, alert: s.pcAlert }}
      onChange={(p) => maj({
        ...(p.enabled !== undefined ? { pcEnabled: p.enabled } : {}),
        ...(p.keep !== undefined ? { pcKeep: p.keep } : {}),
        ...(p.alert !== undefined ? { pcAlert: p.alert } : {}),
      })}
      etat={props.pc}
      bornes={{ keep: props.bornes.pc_keep, alert: props.bornes.pc_alert_after_days }}
      baisses={baissesPc}
    />
    </>
  )
}
